export type SlideRgb = { r: number; g: number; b: number };
export type SlideHsv = { h: number; s: number; v: number };

const HEX6 = /^#[0-9a-f]{6}$/i;

export function clampByte(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(255, Math.round(n)));
}

export function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

export function clampHue(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const wrapped = n % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

export function parseSlideRgbHex(raw: string): SlideRgb | null {
  const t = raw.trim();
  if (!HEX6.test(t)) return null;
  return {
    r: parseInt(t.slice(1, 3), 16),
    g: parseInt(t.slice(3, 5), 16),
    b: parseInt(t.slice(5, 7), 16),
  };
}

export function rgbToSlideHex(rgb: SlideRgb): string {
  return `#${[clampByte(rgb.r), clampByte(rgb.g), clampByte(rgb.b)]
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('')}`;
}

export function rgbToSlideHsv(rgb: SlideRgb): SlideHsv {
  const r = clampByte(rgb.r) / 255;
  const g = clampByte(rgb.g) / 255;
  const b = clampByte(rgb.b) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToSlideRgb(hsv: SlideHsv): SlideRgb {
  const h = clampHue(hsv.h);
  const s = clamp01(hsv.s);
  const v = clamp01(hsv.v);
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

export function hexToSlideHsv(hex: string): SlideHsv | null {
  const rgb = parseSlideRgbHex(hex);
  return rgb ? rgbToSlideHsv(rgb) : null;
}

export function hsvToSlideHex(hsv: SlideHsv): string {
  return rgbToSlideHex(hsvToSlideRgb(hsv));
}

export function hueToCssHsl(h: number): string {
  return `hsl(${clampHue(h)} 100% 50%)`;
}
